import { createSelector } from 'reselect'
import { State } from 'interfaces/state'
import { getItemFromCollection, getItemsFromCollection } from 'helpers/normalizeHelpers'
import { CreditCard, CCStatus, CCUid } from 'components/features/creditCard/creditCardStore/types'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { getRequestErrors } from 'helpers/errorHelpers'
import { sortBy } from 'lodash'

export const creditCardAppSelector = (state: State) => state.app.creditCard

export const waitlistRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.waitlistRequest
)

export const createActivateCardRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.activateCardRequest
)

/**========================================================================
 *                           PAYMENT METHOD REQUESTS
 *========================================================================**/

export const lockCardRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.lockCardRequest
)

export const unlockCardRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.unlockCardRequest
)

export const replaceCardRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.replaceCardRequest
)

export const createPaymentMethodRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.createPaymentMethodRequest
)

export const deletePaymentMethodRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.deletePaymentMethodRequest
)

export const lockCardRequestErrorSelector = createSelector(
  lockCardRequestSelector,
  getRequestErrors
)

export const unlockCardRequestErrorSelector = createSelector(
  unlockCardRequestSelector,
  getRequestErrors
)

export const replaceCardRequestErrorSelector = createSelector(
  replaceCardRequestSelector,
  getRequestErrors
)

export const createPaymentMethodErrorSelector = createSelector(
  createPaymentMethodRequestSelector,
  getRequestErrors
)

export const deletePaymentMethodErrorSelector = createSelector(
  deletePaymentMethodRequestSelector,
  getRequestErrors
)

/**========================================================================
 *                           PAYMENT REQUESTS
 *========================================================================**/

export const createPaymentRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.createPaymentRequest
)

export const deletePaymentRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.deletePaymentRequest
)

export const createPaymentErrorSelector = createSelector(
  createPaymentRequestSelector,
  getRequestErrors
)

export const deletePaymentErrorSelector = createSelector(
  deletePaymentRequestSelector,
  getRequestErrors
)

/**========================================================================
 *                           AUTO PAYMENT REQUESTS
 *========================================================================**/

export const createAutoPaymentRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.createAutoPaymentRequest
)

export const deleteAutoPaymentRequestSelector = createSelector(
  creditCardAppSelector,
  (creditCard) => creditCard.deleteAutoPaymentRequest
)

export const createAutoPaymentErrorSelector = createSelector(
  createAutoPaymentRequestSelector,
  getRequestErrors
)

export const deleteAutoPaymentErrorSelector = createSelector(
  deleteAutoPaymentRequestSelector,
  getRequestErrors
)

/**========================================================================
 *                           ALL ERRORS
 *========================================================================**/

export const creditCardIsLoadingSelector = createSelector(
  lockCardRequestSelector,
  replaceCardRequestSelector,
  createPaymentMethodRequestSelector,
  deletePaymentMethodRequestSelector,
  createPaymentRequestSelector,
  deletePaymentRequestSelector,
  createAutoPaymentRequestSelector,
  deleteAutoPaymentRequestSelector,
  (lock, replace, createMethod, deleteMethod, createPayment, deletePayment, createAutoPayment, deleteAutoPayment) => {
    const allItems: boolean[] = [
      lock.isLoading,
      replace.isLoading,
      createMethod.isLoading,
      deleteMethod.isLoading,
      createPayment.isLoading,
      deletePayment.isLoading,
      createAutoPayment.isLoading,
      deleteAutoPayment.isLoading,
    ]
    return allItems.includes(true)
  }
)

export const creditCardErrorsSelector = createSelector(
  lockCardRequestErrorSelector,
  replaceCardRequestErrorSelector,
  createPaymentMethodErrorSelector,
  deletePaymentMethodErrorSelector,
  createPaymentErrorSelector,
  deletePaymentErrorSelector,
  createAutoPaymentErrorSelector,
  deleteAutoPaymentErrorSelector,
  (lock, replace, createMethod, deleteMethod, createPayment, deletePayment, createAutoPayment, deleteAutoPayment) => {
    const allItems: string[] = [
      ...lock,
      ...replace,
      ...createMethod,
      ...deleteMethod,
      ...createPayment,
      ...deletePayment,
      ...createAutoPayment,
      ...deleteAutoPayment,
    ]
    return allItems.filter((item) => item?.length !== 0)
  }
)

/**========================================================================
 *                           DATA SELECTORS
 *========================================================================**/

export const creditCardDataSelector = (state: State) => state.data.creditCard

export const personDataSelector = createSelector(
  creditCardDataSelector,
  (creditCard) => creditCard.person_data ?? null
)

export const onboardingStepsCountSelector = createSelector(
  creditCardDataSelector,
  (card) => card.onboardingStepsCount
)

export const shipmentAddressSelector = createSelector(
  creditCardDataSelector,
  (creditCard) => creditCard.shipmentAddress
)

const cardsSelector = createSelector(
  creditCardDataSelector,
  (creditCard) => creditCard.cards
)

export const createCardSelector = (uid: CCUid) => createSelector(
  cardsSelector,
  (cards) => getItemFromCollection(cards, uid)
)

const activeStatuses = new Set(['active', 'suspended', 'processing', 'compromised'] as CCStatus[])
export const currentCardSelector = createSelector(
  cardsSelector,
  (cardsCollection) => {
    const cards = getItemsFromCollection(cardsCollection)
    if (cards.length === 0) {
      return null
    }

    const extendedCards = cards.map((card) => ({
      ...card,
      statusRatio: +activeStatuses.has(card.status),
      createdDate: moment(card.expiration).valueOf(),
    }))
    const { statusRatio, ...currentCard } = orderBy(extendedCards, ['statusRatio', 'createdDate'], ['desc', 'desc'])[0] // Grab the first card
    return currentCard as CreditCard
  }
)

export const nextCardSelector = createSelector(
  cardsSelector,
  (cardsCollection) => {
    const cards = getItemsFromCollection(cardsCollection)
    if (cards.length === 0) {
      return null
    }

    const extendedCards = cards.map((card) => ({
      ...card,
      statusRatio: +activeStatuses.has(card.status),
      createdDate: moment(card.expiration).valueOf(),
    }))
    const secondCard = orderBy(extendedCards, ['statusRatio', 'createdDate'], ['desc', 'desc'])[1]
    if (!secondCard) {
      return null
    }

    const { statusRatio, ...cardDetails } = secondCard
    return cardDetails as CreditCard
  }
)

export const currentCardStatusSelector = createSelector(
  currentCardSelector,
  (card) => {
    if (!card) {
      return null
    }

    return card.status
  }
)

export const currentCCLedgerSelector = createSelector(
  currentCardSelector,
  creditCardDataSelector,
  (card, creditCardState) => {
    if (!card) {
      return null
    }

    return getItemFromCollection(creditCardState.ledgers, card.ledgerUid)
  }
)

export const cardPaymentMethodsSelector = createSelector(
  creditCardDataSelector,
  (creditCard) => {
    const paymentMethods = getItemsFromCollection(creditCard.paymentMethods)
    return paymentMethods.length > 0 ? paymentMethods : null
  }
)

export const createCardPaymentMethodSelector = (uid: CCUid) => createSelector(
  creditCardDataSelector,
  (creditCardData) => getItemFromCollection(creditCardData.paymentMethods, uid)
)

export const cardUpcomingPaymentsSelector = createSelector(
  creditCardDataSelector,
  (creditCard) => {
    const payments = getItemsFromCollection(creditCard.upcomingPayments)
    return payments.length > 0 ? sortBy(payments, 'payment_date') : null
    // return [...payments, CREDIT_CARD_AUTO_PAYMENT, CREDIT_CARD_PAYMENT] // Debug Dev Response
  }
)

export const createCardUpcomingPaymentSelector = (uid: CCUid) => createSelector(
  creditCardDataSelector,
  (creditCardData) => getItemFromCollection(creditCardData.upcomingPayments, uid)
)
export const cardPaymentsSelector = createSelector(
  creditCardDataSelector,
  (creditCard) => {
    const payments = getItemsFromCollection(creditCard.payments)
    if (payments.length === 0) {
      return null
    }

    return payments
    // return [CREDIT_CARD_PAYMENT_TRANSACTION] // Debug Dev Response
  }
)

export const createCardPaymentSelector = (uid: CCUid) => createSelector(
  creditCardDataSelector,
  (creditCardData) => getItemFromCollection(creditCardData.payments, uid)
)
